import React, { useState, useRef, useEffect } from 'react';
import './PauseMenu.css';
import { Play, RotateCcw, Settings2, House } from 'lucide-react';
import Settings from './Settings'; 
import { useTranslation } from 'react-i18next'; 
import { useAudio } from '../utils/AudioContext'; 
import { 
    playUiHoverSound, 
    playUiClickSound, // Resume & Restart
    playUiCloseSound // Home/Quit
} from '../utils/sounds';

function PauseMenu({ onResume, onRestart, onQuit }) {
    const { t } = useTranslation(); 
    const { sfxMuted } = useAudio();
    const [isExiting, setIsExiting] = useState(false);
    const [isSettingsOpen, setSettingsOpen] = useState(false); 
    const actionRef = useRef(null);

    const handleAction = (action) => {
        actionRef.current = action;
        setIsExiting(true);
    };

    const handleAnimationEnd = (e) => {
        if (isExiting && actionRef.current && e.animationName.includes('popOut')) {
            actionRef.current();
        }
    };

    const openSettings = () => {
        playUiClickSound(sfxMuted);
        setSettingsOpen(true);
    };

    const closeSettings = () => {
        setSettingsOpen(false);
    };

    // Listen for Escape key to resume game (play exit animation)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (isExiting) return;
                if (isSettingsOpen) return; // Let Settings component handle it
                
                // Play click sound when resuming via ESC
                playUiClickSound(sfxMuted);
                handleAction(onResume);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isExiting, isSettingsOpen, onResume, sfxMuted]);

    const isResuming = actionRef.current === onResume;

    return (
        <>
            <div className={`pause-container ${isExiting && !isResuming ? 'fade-out' : ''}`}>
                <div 
                    className={`pause-modal ${isExiting ? 'pop-out' : ''}`}
                    onAnimationEnd={handleAnimationEnd}
                >
                    <h1 className="modal-title">
                        {t('paused').split('').map((char, index) => {
                            const colors = ['c-green', 'c-blue', 'c-red', 'c-yellow'];
                            const color = colors[index % colors.length];
                            return <span key={index} className={color}>{char}</span>;
                        })}
                    </h1>

                    <div className="actions-stack">
                        {/* Resume: Click Sound */}
                        <button 
                            className="btn btn-resume" 
                            onClick={() => {
                                playUiClickSound(sfxMuted);
                                handleAction(onResume);
                            }}
                            onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        >
                            <Play />
                            {t('resume')}
                        </button>

                        <div className="secondary-actions">
                            {/* Restart: Click Sound */}
                            <button 
                                className="btn btn-icon-only btn-restart" 
                                aria-label={t('restart_level')} 
                                onClick={() => {
                                    playUiClickSound(sfxMuted);
                                    handleAction(onRestart);
                                }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                <RotateCcw />
                            </button>
                            
                            {/* Settings: Click Sound */}
                            <button 
                                className="btn btn-icon-only btn-settings" 
                                aria-label={t('settings')} 
                                onClick={openSettings}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                <Settings2 />
                            </button>
                            
                            {/* Quit (Home): Close Sound */}
                            <button 
                                className="btn btn-icon-only btn-quit" 
                                aria-label={t('quit_to_menu')} 
                                onClick={() => {
                                    playUiCloseSound(sfxMuted);
                                    handleAction(onQuit);
                                }}
                                onMouseEnter={() => playUiHoverSound(sfxMuted)}
                            >
                                <House />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {isSettingsOpen && <Settings onClose={closeSettings} />}
        </>
    );
}

export default PauseMenu;